import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { loadRebootHistory } from '@/redux/actions/productDetail';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

import * as Styles from './VpsOciDetailPage.styles';

import useLocale from '@/hooks/useLocale';
import { useIsMobile } from '@/hooks/useIsMobile';

import { differenceInMilliseconds, parseISO } from 'date-fns';
import { Skeleton } from 'gatorcomponents';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  ENABLE_VPS_OCI_GUIDE_TOUR,
  ENABLE_VPS_OCI_NEW_SCREEN_UPGRADE,
  ENABLE_VPS_OCI_NEW_SCREEN_UPGRADE_V2,
  ENABLE_VPS_OCI_GATORCLAW_CONFIGURE,
  ENABLE_VPS_OCI_SSH_KEY_SCREEN,
} from '@/config/GrowthBook/constants';

import { billingAnalytics } from '@/analytics';

import { VPS_OCI_PAGE_STEPS } from './VpsOciDetailPage.enum';
import { VpsOciStatusEnum } from '@/pages/Products/products.enum';

import SupportForms from '@/components/SupportForms';
import VpsCustomizer from './components/VpsCustomizer';
import FaqProductDetails from '@/components/FrequentlyQuestions/FaqProductDetails';

import MyPlan from './components/MyPlan/MyPlan';
import VPSInfo from './components/VPSInfo/VPSInfo';
import SSHAccess from './components/SSHAccess/SSHAccess';
import Management from './components/Management/Management';
import ServerManagement from './components/ServerManagement/ServerManagement';
import PlanDetails from './components/PlanDetails/PlanDetails';
import UpgradePlan from './components/PlanDetails/components/UpgradePlan';
import UpgradePlanNew from './components/PlanDetails/components/UpgradePlanNew';
import VpsWelcomeModal from './components/VpsWelcomeModal';
import VpsGuidedTour from './components/VpsGuidedTour';

import ConfirmationOpenClawToken from './pages/OpenClawPage/ConfirmationOpenClawToken';
import ConfigurationGatorClaw from './pages/GatorClawPage/ConfigurationGatorClaw';

import VpsStoreLoaderPage from './pages/VpsStoreLoaderPage/VpsStoreLoaderPage';
import SshKeysPage from './pages/SshKeysPage/SshKeysPage';
import ProductTemplatesPage from './pages/ProductTemplatesPage/ProductTemplatesPage';

import useServerStatus from '@/hooks/useServerStatus';

const TIMER_TO_END_RESTART = 0;

const VpsOciDetailPage = () => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const { id: hostingId } = useParams();

  const token = useSelector(state => state.auth?.token);

  const [rebootingPercentage, setRebootingPercentage] = useState(0);
  const [formToShow, setFormToShow] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [isReviewingTour, setIsReviewingTour] = useState(false);
  const [showProductTemplatesPage, setShowProductTemplatesPage] = useState(false);
  const [showSshKeysPage, setShowSshKeysPage] = useState(false);
  const [currentStep, setCurrentStep] = useState(VPS_OCI_PAGE_STEPS.DETAILS);
  const [showConfigurationOpenClaw, setShowConfigurationOpenClaw] = useState(false);

  const [tourSession, setTourSession] = useState(`${token}-${Date.now()}`);

  const enableVpsOciGuideTour = useFeatureIsOn(ENABLE_VPS_OCI_GUIDE_TOUR);
  const enableVpsOciNewScreenUpgrade = useFeatureIsOn(ENABLE_VPS_OCI_NEW_SCREEN_UPGRADE);
  const enableVpsOciNewScreenUpgradeV2 = useFeatureIsOn(ENABLE_VPS_OCI_NEW_SCREEN_UPGRADE_V2);
  const enableGatorClawConfigure = useFeatureIsOn(ENABLE_VPS_OCI_GATORCLAW_CONFIGURE);
  const enableSshKeyScreen = useFeatureIsOn(ENABLE_VPS_OCI_SSH_KEY_SCREEN);

  const myPlanRef = useRef(null);
  const vpsInfoRef = useRef(null);
  const sshAccessRef = useRef(null);
  const managementRef = useRef(null);
  const hasFetchedStatusRef = useRef(false);
  const wasRebootingRef = useRef(false);
  const wasRebuildingRef = useRef(false);

  const {
    loadingVpsOci,
    isRebooting,
    productName,
    vps,
    packageId,
    canShowTourFlag,
    pendingConfigurations,
    serverInfo,
  } = useSelector(state => ({
    loadingVpsOci: state.vpsOci?.loading,
    isRebooting: state.vpsOci?.isRebooting,
    productName: state.vpsOci?.data?.productName,
    vps: state.vpsOci?.data?.vps,
    packageId: state.vpsOci?.data?.packageId,
    canShowTourFlag: state.vpsOci?.tour?.canShowTour,
    pendingConfigurations: state.vpsOci?.data?.vps?.pendingConfigurations,
    serverInfo: state.vpsOci?.data?.serverInfo,
  }), shallowEqual);

  const isUpgradeDisabled = vps?.hasUpgradeProcess || false;

  const historyBkp = useSelector(state => state.productDetail?.historyBkp ?? [], shallowEqual);
  const loading = loadingVpsOci ?? true;

  const forceRefetchOnMount = !hasFetchedStatusRef.current;

  const {
    serverStatus,
    serverIsLocked,
    refetch: refetchServerStatus,
  } = useServerStatus({
    serverInfo,
    serverId: hostingId,
    serverPackageId: packageId,
    forceRefetch: forceRefetchOnMount,
  });

  if (forceRefetchOnMount) {
    hasFetchedStatusRef.current = true;
  }

  const isVpsOciOnline = serverStatus === VpsOciStatusEnum.ONLINE;
  const isVpsOciProcessing = serverStatus === VpsOciStatusEnum.PROCESSING;
  const isVpsOciRebuild = serverStatus === VpsOciStatusEnum.REBUILD;
  const isVpsRebuilding = isVpsOciProcessing || isVpsOciRebuild || serverIsLocked;

  useEffect(() => {
    if (wasRebootingRef.current && !isRebooting) {
      refetchServerStatus();
    }
    wasRebootingRef.current = isRebooting;
  }, [isRebooting, refetchServerStatus]);

  useEffect(() => {
    if (wasRebuildingRef.current && !isVpsRebuilding) {
      refetchServerStatus();
    }
    wasRebuildingRef.current = isVpsRebuilding;
  }, [isVpsRebuilding, refetchServerStatus]);

  const canShowTour = enableVpsOciGuideTour && canShowTourFlag;
  const canShowVpsOciTour = !loading && canShowTour && isVpsOciOnline && !serverIsLocked;

  const { hostingAndServers: hostingAndServersAnalytics } = billingAnalytics;

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: vpsOciLocale } = productDetailsLocale;

  const hasPendingOpenClawConfiguration = pendingConfigurations?.includes('openclaw');
  const hasPendingGatorClawConfiguration = pendingConfigurations?.includes('gatorclaw');
  const [showConfigurationGatorClaw, setShowConfigurationGatorClaw] = useState(false);

  useEffect(() => {
    if (hostingId) {
      dispatch(vpsOciActions.data.get({ hostingId }));
    }
  }, [dispatch, hostingId]);

  useEffect(() => {
    if (hasPendingOpenClawConfiguration) {
      setShowConfigurationOpenClaw(true);
    } else {
      setShowConfigurationOpenClaw(false);
    }
  }, [hasPendingOpenClawConfiguration]);

  useEffect(() => {
    if (hasPendingGatorClawConfiguration) {
      setShowConfigurationGatorClaw(true);
      if (enableGatorClawConfigure) {
        dispatch(vpsOciActions.gatorClaw.configure(hostingId));
      }
    } else {
      setShowConfigurationGatorClaw(false);
    }
  }, [hasPendingGatorClawConfiguration, enableGatorClawConfigure, dispatch, hostingId]);

  useEffect(() => {
    dispatch(vpsOciActions.rebuild.reset());
    dispatch(vpsOciActions.changeImage.reset());
    dispatch(vpsOciActions.upgrade.reset());
    dispatch(vpsOciActions.sshKeys.fetch.reset());
  }, [dispatch]);

  const timerRef = useRef(null);
  const clearRebootingTimeout = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  const handleRebootingTimer = useCallback(() => {
    if (historyBkp && historyBkp.length > 0) {
      const leftTimerToRestart = TIMER_TO_END_RESTART - differenceInMilliseconds(new Date(), parseISO(historyBkp[0].datetime));

      const percentage = (leftTimerToRestart / TIMER_TO_END_RESTART) * 100;

      setRebootingPercentage(percentage);

      if (leftTimerToRestart > 0) {
        dispatch(vpsOciActions.reboot.loading(true));
        timerRef.current = setTimeout(() => {
          setRebootingPercentage(100);
          dispatch(vpsOciActions.reboot.loading(false));
        }, leftTimerToRestart);
      } else {
        dispatch(vpsOciActions.reboot.loading(false));
      }
    }
  }, [historyBkp, dispatch]);

  const handleShowForm = useCallback((formName) => {
    setFormToShow(formName);
  }, []);

  const handleStartTour = useCallback(() => {
    setShowWelcomeModal(false);
    setTimeout(() => {
      setShouldStartTour(true);
    }, 100);
  }, []);

  const handleReviewTour = useCallback(() => {
    setTourSession(`${token}-${Date.now()}`);
    setShouldStartTour(true);
    setIsReviewingTour(true);
  }, [token]);

  const handleTourClose = useCallback(() => {
    setShouldStartTour(false);
    setTourSession(null);
  }, []);

  const saveTourDismissed = useCallback(() => {
    !isReviewingTour && dispatch(vpsOciActions.tour.saveStep({ step: 'tour_dismissed', hostingId, session: tourSession }));
    setShowWelcomeModal(false);
    setTourSession(null);
  }, [dispatch, isReviewingTour, hostingId, tourSession]);

  useEffect(() => {
    handleRebootingTimer();
    return () => clearRebootingTimeout();
  }, [historyBkp, dispatch, clearRebootingTimeout, handleRebootingTimer]);

  useEffect(() => {
    if (hostingId) {
      dispatch(vpsOciActions.tour.get({ hostingId }));
    }
  }, [dispatch, hostingId]);

  useEffect(() => {
    if (canShowVpsOciTour) {
      !tourSession && setTourSession(`${token}-${Date.now()}`);
      setShowWelcomeModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowVpsOciTour, token]);

  const handleLoadRebootHistory = useCallback(() => {
    if (hostingId) {
      dispatch(loadRebootHistory(hostingId));
    }
  }, [hostingId, dispatch]);

  const handleNavigateToStep = useCallback((step) => {
    if (enableVpsOciNewScreenUpgrade) {
      setCurrentStep(step);
    } else {
      handleShowForm('upgradePlan');
    }
  }, [enableVpsOciNewScreenUpgrade, handleShowForm]);

  const handleNavigateToTemplatesPage = useCallback(() => {
    hostingAndServersAnalytics.hostingManagementSoApplicationsClick(productName);

    setShowProductTemplatesPage(true);
  }, [productName, hostingAndServersAnalytics]);

  const handleNavigateToSshKeysPage = useCallback(() => {
    setShowSshKeysPage(true);
  }, []);

  useEffect(() => {
    handleLoadRebootHistory();
  }, [handleLoadRebootHistory]);

  if (loading) {
    return (
      <Styles.Container data-testid="vpsoci-detail-page-loading">
        <Styles.Row>
          <Skeleton width="100%" height="116px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="470px" />
          <Skeleton width="100%" height="470px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="178px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="144px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="116px" />
          <Skeleton width="100%" height="116px" />
        </Styles.Row>
      </Styles.Container>
    );
  }

  if (!vps || Object.keys(vps).length === 0) return null;

  if (productName) {
    const getProductNamePackageVPSOci = (productName && vps.domain) ? `${productName} - ${vps.domain}` : '';

    if (formToShow === 'upgradePlan') {
      return (
        <SupportForms
          serviceType={{
            title: vpsOciLocale.planDetails.form.title,
            onSuccess: () => setFormToShow(''),
            onBackForm: () => setFormToShow(''),
            lockedOptions: getProductNamePackageVPSOci !== '',
            defaultValues: {
              defaultSelectedProductOption: getProductNamePackageVPSOci,
              defaultSelectedSector: 'billing',
              defaultSelectedNeed: vpsOciLocale.planDetails.form.defaultSelectedNeed,
            },
          }}
        />
      );
    }

    if (formToShow === 'changePlan') {
      return (
        <SupportForms
          serviceType={{
            title: vpsOciLocale.vpsCustomizer.form.title,
            onSuccess: () => setFormToShow(''),
            onBackForm: () => setFormToShow(''),
            lockedOptions: true,
            defaultValues: {
              defaultSelectedProductOption: getProductNamePackageVPSOci,
              defaultSelectedSector: 'billing',
              defaultSelectedNeed: vpsOciLocale.vpsCustomizer.form.defaultSelectedNeed,
            },
          }}
        />
      );
    }
  }

  if (isRebooting) {
    return (
      <VpsStoreLoaderPage
        progress={rebootingPercentage}
        title={vpsOciLocale.myPlan.feedback.loading}
      />
    );
  }

  if (isVpsRebuilding) {
    return (
      <VpsStoreLoaderPage
        title={vpsOciLocale.store.rebuild.feedback.request.title}
        description={vpsOciLocale.store.rebuild.feedback.request.description}
        isLoaderBuilder
      />
    );
  }

  if (showConfigurationGatorClaw) {
    return (
      <Styles.Container>
        <ConfigurationGatorClaw
          onShowConfigurationGatorClaw={setShowConfigurationGatorClaw}
        />
      </Styles.Container>
    );
  }

  if (showConfigurationOpenClaw) {
    return (
      <Styles.Container>
        <ConfirmationOpenClawToken
          onShowConfigurationOpenClaw={setShowConfigurationOpenClaw}
        />
      </Styles.Container>
    );
  }

  if (showProductTemplatesPage) {
    return (
      <ProductTemplatesPage
        setShowProductTemplatesPage={setShowProductTemplatesPage}
      />
    );
  }

  if (showSshKeysPage && enableSshKeyScreen) {
    return (
      <SshKeysPage
        setShowSshKeysPage={setShowSshKeysPage}
      />
    );
  }

  if (currentStep === VPS_OCI_PAGE_STEPS.UPGRADE_LISTING || currentStep === VPS_OCI_PAGE_STEPS.UPGRADE_DETAILS) {
    return (
      <Styles.Container data-testid="vpsoci-detail-upgrade-page">
        {enableVpsOciNewScreenUpgradeV2 ? (
          <UpgradePlanNew
            currentStep={currentStep}
            onNavigateToStep={handleNavigateToStep}
          />
        ) : (
          <UpgradePlan
            currentStep={currentStep}
            onNavigateToStep={handleNavigateToStep}
          />
        )}
      </Styles.Container>
    );
  }

  return (
    <Styles.Container data-testid="vpsoci-detail-page">

      <Styles.Row>
        <MyPlan ref={myPlanRef} />
      </Styles.Row>

      <Styles.Row>
        <Styles.Collumn>

          <VPSInfo ref={vpsInfoRef} />

          <Management
            ref={managementRef}
            onGoToTemplatesPage={handleNavigateToTemplatesPage}
          />

          <ServerManagement
            onGoToSshKeysPage={handleNavigateToSshKeysPage}
          />

          {isMobile && <SSHAccess ref={sshAccessRef} />}

          {isMobile && (
            <PlanDetails
              onNavigateToStep={handleNavigateToStep}
              disableUpgradeButton={isUpgradeDisabled}
            />
          )}

          <VpsCustomizer
            locales={{
              title: vpsOciLocale.vpsCustomizer.hireBanner.title,
              description: vpsOciLocale.vpsCustomizer.hireBanner.description,
              buttonLabel: vpsOciLocale.vpsCustomizer.hireBanner.button,
            }}
            onGoToTemplatesPage={handleNavigateToTemplatesPage}
          />

        </Styles.Collumn>

        <Styles.Collumn $width={isMobile ? '100%' : '500px'}>
          {!isMobile && <SSHAccess ref={sshAccessRef} />}
          {!isMobile && (
            <PlanDetails
              onNavigateToStep={handleNavigateToStep}
              disableUpgradeButton={isUpgradeDisabled}
            />
          )}
          {packageId && (
            <FaqProductDetails
              packageid={packageId}
              onSecondaryAction={enableVpsOciGuideTour && handleReviewTour}
              secondaryButtonLabel={vpsOciLocale.helpLinks.tourButton}
              showSameWithoutQuestions
              testId="vpsoci-detail-page-faq-product-details"
            />
          )}
        </Styles.Collumn>
      </Styles.Row>

      {showWelcomeModal && (
        <VpsWelcomeModal
          onClose={saveTourDismissed}
          onStartTour={handleStartTour}
          tourSession={tourSession}
        />
      )}

      <VpsGuidedTour
        myPlanRef={myPlanRef.current}
        vpsInfoRef={vpsInfoRef.current}
        sshAccessRef={sshAccessRef.current}
        managementRef={managementRef.current}
        onClose={handleTourClose}
        shouldStart={shouldStartTour}
        isReviewingTour={isReviewingTour}
        saveTourDismissed={saveTourDismissed}
        tourSession={tourSession}
      />
    </Styles.Container>
  );
};

export default VpsOciDetailPage;
